// Option pricing shared by every booking panel: the per-option pricing basis,
// its card suffix, and the window-resolved price (seasonal pricing_rules / the
// Cloudbeds rate calendar) — the same resolve_unit_price engine the hold uses.
// Moved from ServicePanel.tsx with zero behaviour change.

import { useEffect, useState } from "react";
import { supabase } from "../config";
import type { WidgetService, WidgetUnit } from "../types";
import type { BookingWindow } from "./booking";

// Per-OPTION pricing basis: a unit's own pricing_basis wins over the
// service's (per-item platters + per-person tastings in one service).
export function unitPricingBasis(
  service: Pick<WidgetService, "pricing_basis">,
  u: Pick<WidgetUnit, "pricing_basis">,
): "per_item" | "per_person" {
  const isPerPerson = (service.pricing_basis ?? "per_unit") === "per_person";
  return u.pricing_basis ?? (isPerPerson ? "per_person" : "per_item");
}

// Per-option price suffix — accommodation is per night; otherwise driven by
// the option's own basis, so a platter reads "per item" and a tasting reads
// "per person" in the SAME service.
export function optionPriceSuffix(
  service: Pick<WidgetService, "pricing_basis" | "service_type">,
  u: Pick<WidgetUnit, "pricing_basis">,
): string {
  return service.service_type === "accommodation"
    ? " per night"
    : unitPricingBasis(service, u) === "per_person"
      ? " per person"
      : " per item";
}

/**
 * One option's resolved price for one window: [unitId, nightly rands,
 * exact stay total rands | null], or null when the resolver did not answer
 * (callers fall back to base_price).
 */
export async function fetchUnitWindowPrice(
  unitId: string,
  startsAt: string,
  endsAt: string,
  partySize: number,
): Promise<readonly [string, number, number | null] | null> {
  try {
    const { data } = await supabase.rpc("resolve_unit_price", {
      p_service_unit_id: unitId,
      p_starts_at: startsAt,
      p_ends_at: endsAt,
      p_party_size: partySize,
    });
    const r = data as {
      ok?: boolean;
      effective_cents?: number;
      // Cloudbeds calendar results return the exact stay TOTAL as
      // effective_cents (with nights=1 so the charge never
      // re-multiplies); nightly_avg_cents is the per-night figure for
      // display. This map stores NIGHTLY numbers — every consumer
      // multiplies by stayNights — so prefer it when present.
      nightly_avg_cents?: number;
      source?: string;
    } | null;
    if (r?.ok) {
      const isCal = r.source === "cloudbeds_calendar";
      const nightly =
        typeof r.nightly_avg_cents === "number"
          ? r.nightly_avg_cents
          : typeof r.effective_cents === "number"
            ? r.effective_cents
            : null;
      const totalCents = isCal
        ? typeof r.effective_cents === "number"
          ? r.effective_cents
          : null
        : null;
      if (nightly != null) {
        return [unitId, nightly / 100, totalCents != null ? totalCents / 100 : null] as const;
      }
    }
  } catch {
    // ignore — falls back to base_price
  }
  return null;
}

export type UnitWindowPrices = {
  seasonalNightly: Record<string, number>;
  seasonalStayTotal: Record<string, number>;
  /** True only after this exact window has a server quote; never a base-price fallback. */
  isReady: (unitId: string) => boolean;
  hasFailed: (unitId: string) => boolean;
  retry: () => void;
};

// Seasonal DISPLAY pricing. The flat unit.base_price is date-blind, but the
// charge (create_booking_hold) applies date_range pricing_rules — so a summer
// stay would show the winter rate and then be billed the summer one. Resolve
// each option's rules-adjusted price for the CHOSEN window (the same
// resolve_unit_price engine the hold uses) and use it for the estimate + cart
// display. Falls back to base_price with no window/rule, so non-seasonal
// services render identically to before. Debounced + latest-wins (mirrors
// CombosPanel); the real charge is always the hold's amount_cents regardless.
//
// `windowFor` gives an option its OWN window — an option-led panel prices a
// 2-hour session for 2 hours. It is not an effect dependency, so it only ever
// comes paired with `windowSig`, which MUST change whenever any option's
// window changes (it is the effect's re-run key); the type refuses one
// without the other. Omit both = every option priced for `window`, exactly as
// ServicePanel does.
export type UnitWindowPricesInput = {
  units: WidgetUnit[];
  window: BookingWindow | null;
  showPrices: boolean | null | undefined;
} & (
  | { windowFor?: undefined; windowSig?: undefined }
  | { windowFor: (unit: WidgetUnit) => BookingWindow; windowSig: string }
);

export function useUnitWindowPrices({
  units,
  window,
  showPrices,
  windowFor,
  windowSig,
}: UnitWindowPricesInput): UnitWindowPrices {
  const [seasonalNightly, setSeasonalNightly] = useState<Record<string, number>>({});
  // Exact stay totals (rands) for calendar-priced units. A boundary-spanning
  // stay (3,850 + 3,850 + 4,200) has NO clean nightly figure — its average is
  // R3,966.67 — so per-night is display-only (rounded) and every TOTAL a
  // guest sees is this exact sum, never average × nights.
  const [seasonalStayTotal, setSeasonalStayTotal] = useState<Record<string, number>>({});
  const [retryCount, setRetryCount] = useState(0);
  const quoteKey = JSON.stringify([window?.starts_at, window?.ends_at, window?.party_size,
    windowSig ?? "", units.map((unit) => unit.id), retryCount]);
  const [answeredKey, setAnsweredKey] = useState<string | null>(null);
  useEffect(() => {
    if (!window || units.length === 0) {
      setSeasonalNightly({});
      setSeasonalStayTotal({});
      setAnsweredKey(null);
      return;
    }
    if (showPrices === false) return;
    let stale = false;
    const startsAt = window.starts_at;
    const endsAt = window.ends_at;
    const partySize = window.party_size;
    const windowForUnit = windowFor;
    const t = setTimeout(async () => {
      const entries = await Promise.all(
        units.map((u) => {
          const uw = windowForUnit?.(u);
          return fetchUnitWindowPrice(
            u.id,
            uw?.starts_at ?? startsAt,
            uw?.ends_at ?? endsAt,
            uw?.party_size ?? partySize,
          );
        }),
      );
      if (stale) return;
      const map: Record<string, number> = {};
      const totals: Record<string, number> = {};
      for (const e of entries) {
        if (!e) continue;
        map[e[0]] = e[1];
        if (e[2] != null) totals[e[0]] = e[2];
      }
      setSeasonalNightly(map);
      setSeasonalStayTotal(totals);
      setAnsweredKey(quoteKey);
    }, 350);
    return () => {
      stale = true;
      clearTimeout(t);
    };
    // units identity is stable per config load; window fields drive re-resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window?.starts_at, window?.ends_at, window?.party_size, units, showPrices, windowSig ?? "", retryCount]);
  const current = answeredKey === quoteKey;
  return {
    seasonalNightly: current ? seasonalNightly : {},
    seasonalStayTotal: current ? seasonalStayTotal : {},
    isReady: (unitId) => current && Object.prototype.hasOwnProperty.call(seasonalNightly, unitId),
    hasFailed: (unitId) => current && !Object.prototype.hasOwnProperty.call(seasonalNightly, unitId),
    retry: () => setRetryCount((count) => count + 1),
  };
}

// The resolved nightly rate for an option: its seasonal/calendar rate for the
// chosen window if resolved, else the flat base_price (same value the server
// would charge when no rule applies). UNROUNDED — a cart line stores this and
// the drawer compares it against the server's amount_cents to the cent, so a
// R249.50 option rounded to R250 invents a "then at the venue" balance of R2
// on a party of four. Round for display, never for storage.
export function resolvedUnitPrice(prices: UnitWindowPrices, u: WidgetUnit): number {
  return prices.seasonalNightly[u.id] ?? Number(u.base_price ?? 0);
}

// The price to DISPLAY for an option.
export function displayUnitPrice(prices: UnitWindowPrices, u: WidgetUnit): number {
  return Math.round(resolvedUnitPrice(prices, u));
}

// The exact money for a stay on this unit — calendar sum when present,
// else nightly × nights. Totals use THIS; per-night is presentation.
export function stayTotalForUnit(
  prices: UnitWindowPrices,
  u: WidgetUnit,
  stayNights: number,
): number {
  return prices.seasonalStayTotal[u.id] ?? displayUnitPrice(prices, u) * stayNights;
}
